import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { Activity } from '../activity/Activity'
import { Asset } from '../asset/Asset'
import { BaseEntity } from '../base/BaseEntity'
import { Customer } from '../customer/Customer'
import { Payment } from '../payment/Payment'
import { RelatedEntity } from '../base/RelatedEntity'
import { Order } from '../order/Order'
import { generateEntityId } from '../generateEntityId'
import { ReturnOrderItem } from './ReturnOrderItem'
import { ReturnOrderStatusEnum } from './ReturnOrderStatusEnum'
import { ReturnOrderActionEnum } from './ReturnOrderActionEnum'
import { ReturnRefundLeg } from './ReturnRefundLeg'

@ObjectType()
@InputType('ReturnOrderInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class ReturnOrder extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ro') })
  declare _id: string

  @Field(() => ReturnOrderStatusEnum)
  @Prop({ type: String, enum: ReturnOrderStatusEnum })
  declare status: ReturnOrderStatusEnum

  @Field(() => Customer)
  @Prop({ type: Customer })
  by: Customer

  @Field(() => [ReturnOrderItem])
  @Prop({ type: () => [ReturnOrderItem] })
  items: ReturnOrderItem[]

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  net: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  gross: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  taxAmount: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  shippingFee: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  discount: number

  @Field(() => [Payment])
  @Prop({ type: [Payment] })
  payments: Payment[]

  /**
   * Actual-refund execution legs, one per chosen source payment. Seeded Pending on confirm and
   * updated per leg as the gateway calls run; Failed legs are retryable via processReturnRefund.
   */
  @Field(() => [ReturnRefundLeg])
  @Prop({ type: () => [ReturnRefundLeg] })
  refundLegs: ReturnRefundLeg[]

  /**
   * The source sales order's _id this return was created from. Stored explicitly so the source
   * order can be identified directly, rather than inferred from relatedOrders array position
   * (which the relatedOrders field resolver does not preserve).
   */
  @Field(() => String)
  @Prop({ type: String })
  sourceOrderId: string

  /**
   * Links to the source sales order, plus any replacement sales order appended on confirm.
   */
  @Field(() => [Order])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedOrders: ReturnType<() => Order[]>

  @Field(() => [Asset])
  @Prop({ type: [Asset] })
  assets: Asset[]

  @Field(() => [Activity])
  @Prop({ type: [Activity] })
  remarksActivity: Activity[]

  // Derived via field resolver; declared here for type-safety and schema generation.
  @Field(() => ReturnOrderActionEnum)
  returnAction: ReturnOrderActionEnum
}
